import { Sparkle, Spinner, X as XIcon, Database, Receipt } from "@phosphor-icons/react";

const money = (n, cur) => `${cur === "USD" ? "US$" : "$"}${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const COND = { nuevo: "Nuevo", seminuevo: "Seminuevo", reacondicionado: "Reacond.", usado: "Usado" };
const CONF = { alta: "bg-emerald-50 text-emerald-700 border-emerald-200", media: "bg-amber-50 text-amber-700 border-amber-200", baja: "bg-red-50 text-[#E0000F] border-red-200" };

// Panel Alendra para "Inventario y precios": sugiere la lista de precios aprendiendo del histórico.
export default function PriceAIPanel({ ai, currency, onSuggest, onApplyAll, onDiscard }) {
  const d = ai.data;
  return (
    <div className="rounded-md border border-[#7E57C2]/30 bg-[#7E57C2]/5 p-3" data-testid="form-price-ai">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-zinc-700 inline-flex items-start gap-1.5 min-w-0">
          <Sparkle size={13} weight="fill" className="text-[#7E57C2] shrink-0 mt-0.5" />
          <span><b className="text-[#7E57C2]">Alendra AI</b> propone la lista de precios aprendiendo de productos similares que ya tienes, lo que realmente se vendió y estudios de mercado previos.</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {d && ai.applied.length > 0 && (
            <button type="button" onClick={onDiscard} className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] text-zinc-600 border border-zinc-200 hover:bg-zinc-50 bg-white" data-testid="form-price-ai-discard">
              <XIcon size={11} weight="bold" /> Descartar
            </button>
          )}
          {d && (
            <button type="button" onClick={onApplyAll} className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-semibold text-[#7E57C2] border border-[#7E57C2]/40 hover:bg-white bg-white/70" data-testid="form-price-ai-apply-all">
              Reemplazar los 4 precios
            </button>
          )}
          <button type="button" onClick={onSuggest} disabled={ai.loading}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-semibold text-white bg-[#7E57C2] hover:bg-[#6a46ad] disabled:opacity-60"
            data-testid="form-price-ai-btn">
            {ai.loading ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />} {d ? "Volver a sugerir" : "Sugerir precios con IA"}
          </button>
        </div>
      </div>

      {d && (
        <div className="mt-3 space-y-2.5" data-testid="form-price-ai-result">
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className={`px-1.5 py-0.5 rounded border font-semibold uppercase tracking-wider text-[10px] ${CONF[d.confidence] || CONF.media}`} data-testid="form-price-ai-confidence">Confianza {d.confidence}</span>
            <span className="text-zinc-600 inline-flex items-center gap-1"><Database size={11} /> {d.comparables.length} comparable(s) · {d.same_category_count} en la categoría</span>
            {d.sold_median > 0 && <span className="text-zinc-600 inline-flex items-center gap-1"><Receipt size={11} /> Venta real mediana {money(d.sold_median, currency)}</span>}
            {d.cost_in_sale_currency > 0 && <span className="text-zinc-500">Costo ≈ {money(d.cost_in_sale_currency, currency)} (TC {d.exchange_rate})</span>}
          </div>
          {d.notes && <p className="text-[11px] text-[#7E57C2]" data-testid="form-price-ai-notes">{d.notes}</p>}
          {d.comparables.length > 0 && (
            <div className="overflow-x-auto rounded border border-[#7E57C2]/20 bg-white" data-testid="form-price-ai-comps">
              <table className="w-full text-[11px]">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                  <tr><th className="text-left px-2 py-1 font-semibold">Comparable</th><th className="text-left px-2 py-1 font-semibold">Cond.</th><th className="text-right px-2 py-1 font-semibold">Costo</th><th className="text-right px-2 py-1 font-semibold">Mín</th><th className="text-right px-2 py-1 font-semibold">Tienda</th><th className="text-right px-2 py-1 font-semibold">Vendido</th></tr>
                </thead>
                <tbody>
                  {d.comparables.map((c) => (
                    <tr key={c.id} className={`border-t border-zinc-100 ${d.based_on?.includes(c.id.slice(0, 8)) ? "bg-[#7E57C2]/5" : ""}`} data-testid={`form-price-ai-comp-${c.id}`}>
                      <td className="px-2 py-1 max-w-[220px] truncate" title={c.name}>{c.name} <span className="text-zinc-400">· {c.brand || "—"} · stock {c.stock}</span></td>
                      <td className="px-2 py-1 text-zinc-600">{COND[c.condition] || c.condition || "—"}</td>
                      <td className="px-2 py-1 text-right font-mono-data">{money(c.cost, currency)}</td>
                      <td className="px-2 py-1 text-right font-mono-data">{money(c.min_price, currency)}</td>
                      <td className="px-2 py-1 text-right font-mono-data font-semibold">{money(c.store_price, currency)}</td>
                      <td className="px-2 py-1 text-right font-mono-data">{c.sold_qty ? <span className="text-emerald-700">{c.sold_qty}u · {money(c.avg_sold, currency)}</span> : <span className="text-zinc-400">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {Object.keys(d.reasons || {}).length > 0 && (
            <ul className="space-y-0.5 text-[11px] text-zinc-600" data-testid="form-price-ai-reasons">
              {d.reasons.min_price && <li><b className="text-zinc-800">Mínimo:</b> {d.reasons.min_price}</li>}
              {d.reasons.reseller_price && <li><b className="text-zinc-800">Reventa:</b> {d.reasons.reseller_price}</li>}
              {d.reasons.marketplace_price && <li><b className="text-zinc-800">Distribuidor:</b> {d.reasons.marketplace_price}</li>}
              {d.reasons.store_price && <li><b className="text-zinc-800">Tienda:</b> {d.reasons.store_price}</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
